import { useMemo, useState, type ReactNode } from 'react';
import {
  ArrowBack,
  DeleteOutline,
  Email,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_APPROVE_STATUS, userApproveStatuses } from '@/config/constants';
import {
  useAddAdminNoteMutation,
  useDeleteAdminNoteMutation,
  useGetAdminStaffQuery,
  useGetAdminUserOverviewQuery,
  type AdminTimelineEvent,
} from '@/apis/extended/admin/adminWorkspaceApi';
import { useUpdateUserByAdminMutation } from '@/apis/extended/admin/userApi';
import { AdminMessageDialog } from '@/components/Admin/AdminWorkspace/AdminMessageDialog';
import {
  AttestationChip,
  daysAgoLabel,
  DocumentsSummary,
  formatMoney,
  humanizeKey,
} from '@/components/Admin/AdminWorkspace/loanHealth';
import { BuyerUploadedDocuments } from '@/components/Admin/Documents/BuyerUploadedDocuments';

const TIMELINE_KINDS: {
  key: AdminTimelineEvent['kind'] | 'all';
  label: string;
}[] = [
  { key: 'all', label: 'Everything' },
  { key: 'loan', label: 'Loan files' },
  { key: 'contract', label: 'Contracts' },
  { key: 'property', label: 'Properties' },
  { key: 'document', label: 'Documents' },
  { key: 'message', label: 'Messages' },
  { key: 'note', label: 'Notes' },
  { key: 'account', label: 'Account' },
];

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString() : '—';
const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString() : '—';

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

/**
 * One page per person: who they are, where they are in the funnel, and
 * everything tied to them — loan files, properties, contracts, documents,
 * messages, internal notes and a timeline.
 */
const AdminUserProfilePage = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetAdminUserOverviewQuery(userId, {
    skip: !userId,
  });
  const { data: staff = [] } = useGetAdminStaffQuery();
  const [addNote, { isLoading: savingNote }] = useAddAdminNoteMutation();
  const [deleteNote] = useDeleteAdminNoteMutation();
  const [updateUser] = useUpdateUserByAdminMutation();
  const [note, setNote] = useState('');
  const [tab, setTab] = useState(0);
  const [timelineKind, setTimelineKind] =
    useState<(typeof TIMELINE_KINDS)[number]['key']>('all');
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageFileId, setMessageFileId] = useState<string | null>(null);

  const staffName = useMemo(() => {
    const m: Record<string, string> = {};
    staff.forEach((s) => (m[s._id] = s.name));
    return m;
  }, [staff]);

  const timeline = useMemo(
    () =>
      (data?.timeline ?? []).filter(
        (e) => timelineKind === 'all' || e.kind === timelineKind
      ),
    [data?.timeline, timelineKind]
  );

  if (isLoading) return <LinearProgress />;
  if (error || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Could not load this person.{' '}
        <Button onClick={() => navigate('/admin/user')}>Back to Users</Button>
      </Alert>
    );
  }

  const { user, pipeline, loanFiles, properties, contracts, messages, notes } =
    data;
  const firstName = user.displayName.split(' ')[0];
  const info = user.loanInfo || ({} as typeof user.loanInfo);

  const openMessage = (fileId: string | null = null) => {
    setMessageFileId(fileId);
    setMessageOpen(true);
  };

  const saveNote = async () => {
    try {
      await addNote({ userId, body: note }).unwrap();
      setNote('');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Could not save the note');
    }
  };

  const tabs = [
    `Loan files (${loanFiles.length})`,
    `Properties (${properties.length})`,
    `Contracts (${contracts.length})`,
    `Documents (${data.documents.length})`,
    `Messages (${messages.length})`,
    'Timeline',
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/user')}
        sx={{ mb: 1 }}
      >
        Users
      </Button>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} noWrap>
              {user.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email || 'No email'} · {user.phone || 'No phone on file'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip size="small" label={humanizeKey(user.role)} />
              <Chip
                size="small"
                color="primary"
                label={`${pipeline.stageLabel} · ${
                  pipeline.flowType === 'refinance' ? 'Refinance' : 'Purchase'
                }`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Joined ${fmtDate(user.createdAt)}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  user.lastLoginAt
                    ? `Last sign-in ${fmtDate(user.lastLoginAt)}`
                    : 'Never signed in'
                }
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<Email />}
              onClick={() => openMessage(loanFiles[0]?._id ?? null)}
            >
              Message
            </Button>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              component={RouterLink}
              to={`/admin/user/${userId}/view-as`}
            >
              View as user
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 2,
          }}
        >
          {[
            ['Monthly income', formatMoney(info.monthlyIncome)],
            ['Credit score', info.creditScore || '—'],
            ['Monthly debt', formatMoney(info.monthlyDebt)],
            ['Down payment', formatMoney(info.downPayment)],
            ['Approval amount', formatMoney(user.approvalAmount)],
          ].map(([label, value]) => (
            <Box key={String(label)}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography fontWeight={600}>{value}</Typography>
            </Box>
          ))}
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          {/* The pre-qualification decision emailed to the buyer — separate
              from the loan file's own status above. */}
          <TextField
            select
            size="small"
            label="Pre-qualification decision"
            value={user.approveStatus || USER_APPROVE_STATUS.NOT_SUBMITTED}
            onChange={(e) =>
              void updateUser({
                id: userId,
                approveStatus: e.target.value as USER_APPROVE_STATUS,
              })
                .unwrap()
                .catch(() => toast.error('Could not update'))
            }
            sx={{ minWidth: 240 }}
          >
            {userApproveStatuses.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(0, 2fr) minmax(0, 1fr)',
          },
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 1 }}
          >
            {tabs.map((t) => (
              <Tab key={t} label={t} />
            ))}
          </Tabs>

          {tab === 0 && (
            <Section title="Loan files">
              {loanFiles.length === 0 ? (
                <Typography color="text.secondary">
                  No loan application yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Signature</TableCell>
                        <TableCell>Documents</TableCell>
                        <TableCell>Last activity</TableCell>
                        <TableCell>Loan officer</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loanFiles.map((f) => (
                        <TableRow key={f._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {f.flowProfile === 'refinance'
                                ? 'Refinance'
                                : 'Purchase'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {f.address || 'No address yet'}
                            </Typography>
                            {f.insights.stuckReasons.length > 0 && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mt: 0.5 }}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {f.insights.stuckReasons.map((r) => (
                                  <Chip
                                    key={r}
                                    size="small"
                                    color="warning"
                                    label={r}
                                  />
                                ))}
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {humanizeKey(f.insights.lifecycle)}
                          </TableCell>
                          <TableCell>
                            <AttestationChip state={f.insights.attestation} />
                          </TableCell>
                          <TableCell>
                            <DocumentsSummary insights={f.insights} />
                          </TableCell>
                          <TableCell>
                            {daysAgoLabel(f.insights.daysSinceActivity)}
                          </TableCell>
                          <TableCell>
                            {f.assignedToAdminId
                              ? staffName[f.assignedToAdminId] || 'Assigned'
                              : 'Unassigned'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            <Tooltip title="Message about this file">
                              <IconButton
                                size="small"
                                onClick={() => openMessage(f._id)}
                              >
                                <Email fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              component={RouterLink}
                              to={`/admin/loan-pipeline/${f._id}`}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          )}

          {tab === 1 && (
            <Section title="Properties">
              {properties.length === 0 ? (
                <Typography color="text.secondary">
                  No properties saved.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={1}>
                  {properties.map((p) => (
                    <Stack
                      key={p._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {p.address || 'Untitled property'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.transactionMode === 'refinance'
                            ? 'Refinance'
                            : 'Purchase'}{' '}
                          · {formatMoney(p.price)} · added{' '}
                          {fmtDate(p.createdAt)}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/admin/product/${p._id}`}
                      >
                        Open
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Section>
          )}

          {tab === 2 && (
            <Section title="Offers & contracts">
              {contracts.length === 0 ? (
                <Typography color="text.secondary">
                  No offers or contracts.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={1}>
                  {contracts.map((c) => (
                    <Stack
                      key={c._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {c.address || c.contractId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          As {c.side} · {humanizeKey(c.status)} ·{' '}
                          {formatMoney(c.price)}
                          {c.offerGoodUntil
                            ? ` · offer good until ${c.offerGoodUntil}`
                            : ''}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/admin/property/${c._id}`}
                      >
                        Open
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Section>
          )}

          {tab === 3 && (
            <Section title="Documents">
              <BuyerUploadedDocuments
                userId={userId}
                buyerName={user.displayName}
              />
            </Section>
          )}

          {tab === 4 && (
            <Section
              title="Messages"
              action={
                <Button
                  size="small"
                  startIcon={<Email />}
                  onClick={() => openMessage(null)}
                >
                  New message
                </Button>
              }
            >
              {messages.length === 0 ? (
                <Typography color="text.secondary">
                  No emails sent yet.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={1}>
                  {messages.map((m) => (
                    <Box key={m._id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {m.subject || humanizeKey(m.templateKey)}
                        </Typography>
                        <Chip
                          size="small"
                          label={m.status}
                          color={m.status === 'failed' ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDateTime(m.sentAt)} ·{' '}
                        {m.sentBy ? `by ${m.sentBy}` : 'automatic'}
                      </Typography>
                      {m.body && (
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                          color="text.secondary"
                        >
                          {m.body}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Section>
          )}

          {tab === 5 && (
            <Section title="Timeline">
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ mb: 1.5 }}
                flexWrap="wrap"
                useFlexGap
              >
                {TIMELINE_KINDS.map((k) => (
                  <Chip
                    key={k.key}
                    size="small"
                    label={k.label}
                    color={timelineKind === k.key ? 'primary' : 'default'}
                    variant={timelineKind === k.key ? 'filled' : 'outlined'}
                    onClick={() => setTimelineKind(k.key)}
                  />
                ))}
              </Stack>
              {timeline.length === 0 ? (
                <Typography color="text.secondary">
                  Nothing here yet.
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {timeline.map((e, i) => (
                    <Box
                      key={`${e.at}-${i}`}
                      sx={{ display: 'flex', gap: 1.5 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 92, flexShrink: 0 }}
                      >
                        {fmtDateTime(e.at)}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">
                          {humanizeKey(e.title)}
                          {e.actor ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {' '}
                              · {e.actor}
                            </Typography>
                          ) : null}
                        </Typography>
                        {e.detail && (
                          <Typography variant="caption" color="text.secondary">
                            {e.detail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Section>
          )}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Section title="Internal notes">
            <Typography variant="caption" color="text.secondary">
              Only admins see these.
            </Typography>
            <TextField
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note…"
              multiline
              minRows={2}
              fullWidth
              size="small"
              sx={{ my: 1 }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={!note.trim() || savingNote}
              onClick={() => void saveNote()}
            >
              Save note
            </Button>
            <Stack spacing={1} sx={{ mt: 2 }} divider={<Divider flexItem />}>
              {notes.map((n) => (
                <Box key={n._id} sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {n.body}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.authorName || 'Admin'} · {fmtDateTime(n.createdAt)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Delete note"
                    onClick={() => void deleteNote({ userId, noteId: n._id })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Section>

          <Section title="Recent activity">
            <Stack spacing={1}>
              {data.timeline.slice(0, 6).map((e, i) => (
                <Box key={`${e.at}-r${i}`}>
                  <Typography variant="body2">
                    {humanizeKey(e.title)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDateTime(e.at)}
                  </Typography>
                </Box>
              ))}
              <Button size="small" onClick={() => setTab(5)}>
                Full timeline
              </Button>
            </Stack>
          </Section>
        </Box>
      </Box>

      <AdminMessageDialog
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        userId={userId}
        firstName={firstName}
        email={user.email}
        loanFiles={loanFiles}
        applicationId={messageFileId}
      />
    </Box>
  );
};

export { AdminUserProfilePage };
